import { internalLink } from "@/sanity/defaults/link.defaults";

const portableText = (text: string, key: string) => [
  {
    _type: "block" as const,
    _key: key,
    style: "normal" as const,
    markDefs: [] as never[],
    children: [
      {
        _type: "span" as const,
        _key: `${key}-span`,
        text,
        marks: [] as string[],
      },
    ],
  },
];

export const FREE_QUOTE_DRAWER_CONSENT_TEXT =
  "By clicking the 'Get a Free Quote' button below, I agree that an ADT specialist may contact me via text messages, phone calls, or artificial or pre-recorded messages, from time to time, to the phone number provided by me using automated technology about ADT offers and consent is not required to make a purchase. Your information is collected and used in accordance with our privacy policy. Msg & data rates apply.";

export const FREE_QUOTE_DRAWER_CONTENT = {
  triggerLabel: "Get a free quote",
  triggerAriaLabel: "Get a free quote",
  heading: "Prefer a helping hand?",
  callout: "Get a free quote",
  intro: [
    {
      _type: "block" as const,
      _key: "intro",
      style: "normal" as const,
      markDefs: [] as never[],
      children: [
        {
          _type: "span" as const,
          _key: "intro-before",
          text: "Call ",
          marks: [] as string[],
        },
        {
          _type: "inlinePhoneNumber" as const,
          _key: "intro-phone",
        },
        {
          _type: "span" as const,
          _key: "intro-after",
          text: " or complete the form and an ADT specialist will contact you with a quote.",
          marks: [] as string[],
        },
      ],
    },
  ],
  requiredFieldsMessage: "All fields marked with an asterisk (*) are required.",
  nameLabel: "Name",
  namePlaceholder: "John Smith",
  emailLabel: "Email",
  emailPlaceholder: "john@example.com",
  phoneFieldLabel: "Phone",
  phonePlaceholder: "(___) ___-____",
  zipLabel: "ZIP",
  zipPlaceholder: "00000",
  installationPreferences: [
    {
      id: "difm",
      label: "Professional installation",
      mobileLabel: "Pro installation",
      description: "An ADT pro installs and sets up your system for you",
    },
    {
      id: "diy",
      label: "DIY installation",
      mobileLabel: "DIY installation",
      description:
        "Set it up yourself in about 30 minutes, with help a call away",
    },
    {
      id: "difm_diy",
      label: "Not sure yet",
      mobileLabel: "Not sure yet, help me decide",
      description: "",
    },
  ],
  consentText: portableText(FREE_QUOTE_DRAWER_CONSENT_TEXT, "consent"),
  submitLabel: "GET A FREE QUOTE",
  successHeading: "Thank You!",
  successMessage: "We will contact you shortly.",
  nextStepsHeading: "What happens next?",
  nextSteps: [
    {
      icon: "headset",
      title: "A call from us",
      description: "Connect with an ADT pro to discuss your needs",
    },
    {
      icon: "cameras",
      title: "Product assistance",
      description: "Explore personalized home security solutions",
    },
    {
      icon: "quote",
      title: "Your custom quote",
      description: "Compare features and pricing to find your best fit",
    },
  ],
  skipWaitHeading: "Skip the wait by calling!",
  skipWaitMessage:
    "We are here to help you lock in your ideal security package. If you’d like to speak with us sooner, call us.",
  skipWaitMessageCompact:
    "If you’d like to speak with us sooner, call us anytime",
  shopCta: {
    label: "Shop now",
    compactLabel: "Shop packages",
    link: internalLink("/blu/shop"),
  },
};

export type FreeQuoteDrawerDefaults = typeof FREE_QUOTE_DRAWER_CONTENT;

type NextStep = FreeQuoteDrawerDefaults["nextSteps"][number];

const omitNullish = <T extends Record<string, unknown>>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => item != null),
  ) as Partial<T>;

export function mergeFreeQuoteDrawerContent(
  data: Partial<FreeQuoteDrawerDefaults> | null | undefined,
): FreeQuoteDrawerDefaults {
  if (!data) return FREE_QUOTE_DRAWER_CONTENT;

  const {
    nextSteps: rawSteps,
    shopCta,
    installationPreferences: rawPreferences,
    ...rest
  } = data;
  const nextSteps = (rawSteps ?? []).filter((step): step is NextStep =>
    Boolean(step?.title && step.description),
  );
  const installationPreferences = (rawPreferences ?? []).filter((option) =>
    Boolean(option?.id && option.label && option.mobileLabel),
  );

  return {
    ...FREE_QUOTE_DRAWER_CONTENT,
    ...omitNullish(rest as Record<string, unknown>),
    intro: FREE_QUOTE_DRAWER_CONTENT.intro,
    successHeading: FREE_QUOTE_DRAWER_CONTENT.successHeading,
    successMessage: FREE_QUOTE_DRAWER_CONTENT.successMessage,
    installationPreferences: installationPreferences.length
      ? installationPreferences
      : FREE_QUOTE_DRAWER_CONTENT.installationPreferences,
    nextSteps: nextSteps.length
      ? nextSteps
      : FREE_QUOTE_DRAWER_CONTENT.nextSteps,
    shopCta: {
      ...FREE_QUOTE_DRAWER_CONTENT.shopCta,
      ...omitNullish((shopCta ?? {}) as Record<string, unknown>),
      link: shopCta?.link ?? FREE_QUOTE_DRAWER_CONTENT.shopCta.link,
    },
  };
}
